import React from "react";

/* Shared atoms reused across all reports (Product & IT, Business, Operations). */

export const Meta = ({ label, value, sub }) => (
    <div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-white/55 font-semibold">
            {label}
        </div>
        <div className="mt-2 text-xl sm:text-2xl font-[700] tracking-[-0.02em]">
            {value}
        </div>
        {sub && <div className="text-xs text-white/55 mt-1">{sub}</div>}
    </div>
);

export const Stat = ({ n, label }) => (
    <div>
        <div className="stat-num">
            {n}
            <span className="text-[var(--fora-red-1)]">.</span>
        </div>
        <div className="mt-2 text-[var(--fora-muted)] text-sm leading-snug">
            {label}
        </div>
    </div>
);

export const Person = ({ name, role, img, email }) => (
    <div
        className="border border-white/15 p-5 hover:border-[var(--fora-red-1)] transition-colors flex items-start gap-4"
        data-testid={`person-${name.toLowerCase().replace(/\s+/g, "-")}`}
        itemScope
        itemType="https://schema.org/Person"
    >
        {img ? (
            <img
                src={img}
                alt={name}
                className="w-14 h-14 rounded-full object-cover border border-white/20 shrink-0"
                itemProp="image"
            />
        ) : (
            <div
                className="w-14 h-14 rounded-full bg-[var(--fora-red-1)] flex items-center justify-center font-[800] text-base text-white shrink-0"
                aria-hidden="true"
            >
                {name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)}
            </div>
        )}
        <div className="min-w-0">
            <div
                className="text-[10px] tracking-[0.22em] uppercase text-white/55 font-bold"
                itemProp="jobTitle"
            >
                {role}
            </div>
            <div
                className="mt-1 text-base font-[700] tracking-[-0.02em] truncate"
                itemProp="name"
            >
                {name}
            </div>
            {email && (
                <a
                    href={`mailto:${email}`}
                    className="mt-1 inline-block text-[11px] text-white/55 hover:text-[var(--fora-red-1)] transition-colors break-all"
                    data-testid="person-email"
                    itemProp="email"
                >
                    {email}
                </a>
            )}
            <meta itemProp="worksFor" content="Master F&B d.o.o." />
        </div>
    </div>
);

export const FlowStep = ({ children, highlight = false }) => (
    <span
        className={`inline-flex items-center px-3 py-2 border ${
            highlight
                ? "bg-[var(--fora-red-1)] border-[var(--fora-red-1)] text-white"
                : "border-white/25 text-white"
        }`}
    >
        {children}
    </span>
);

export const FlowArrow = () => (
    <span className="hidden sm:inline-flex text-white/40 text-2xl leading-none select-none">
        →
    </span>
);

export const StatusPill = ({ status }) => {
    const map = {
        "On Track": "bg-[var(--fora-red-1)] text-white",
        Pending: "bg-[var(--fora-ink)] text-white",
        Open: "bg-[var(--fora-gray)] text-[var(--fora-ink)] border border-[var(--fora-ink)]",
        Done: "bg-white text-[var(--fora-ink)] border border-[var(--fora-ink)]",
        Blocked: "bg-[var(--fora-red-2)] text-white",
        Paused: "bg-[var(--fora-gray-dark)] text-white",
        "In Progress": "bg-[var(--fora-red-1)] text-white",
        "Not Started": "bg-[var(--fora-gray)] text-[var(--fora-ink)] border border-[var(--fora-ink)]",
    };
    const cls = map[status] || map["Open"];
    return (
        <span
            className={`inline-flex items-center px-2 py-1 text-[9px] font-bold tracking-[0.18em] uppercase ${cls}`}
            data-testid="status-pill"
        >
            {status}
        </span>
    );
};

export const KpiBlock = ({ eyebrow, title, subtitle, rows, testIdPrefix }) => (
    <div data-testid={`${testIdPrefix}-block`}>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
            <div>
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                    {eyebrow}
                </div>
                <h3 className="mt-2 font-[700] text-2xl sm:text-3xl tracking-[-0.02em] leading-tight">
                    {title}
                </h3>
            </div>
            <p className="text-white/55 text-sm max-w-md">{subtitle}</p>
        </div>

        {/* Desktop table */}
        <div
            className="hidden lg:block border border-white/15"
            data-testid={`${testIdPrefix}-table`}
        >
            <div className="grid grid-cols-12 px-6 py-4 bg-white/[0.04] text-[10px] font-bold tracking-[0.22em] uppercase text-white/55 border-b border-white/10">
                <div className="col-span-3">KPI</div>
                <div className="col-span-4">Definition</div>
                <div className="col-span-1">Target</div>
                <div className="col-span-2">Current Status</div>
                <div className="col-span-2">Next Review Focus</div>
            </div>
            {rows.map((k, i) => (
                <div
                    key={k.n}
                    className={`grid grid-cols-12 px-6 py-6 gap-4 ${
                        i !== rows.length - 1 ? "border-b border-white/10" : ""
                    } hover:bg-white/[0.03] transition-colors`}
                    data-testid={`${testIdPrefix}-row-${k.n}`}
                >
                    <div className="col-span-3 flex gap-4 items-start">
                        <span className="text-[var(--fora-red-1)] font-[800] text-xl tracking-[-0.04em] leading-none">
                            {k.n}
                        </span>
                        <span className="font-[700] text-lg tracking-[-0.01em] leading-snug">
                            {k.name}
                        </span>
                    </div>
                    <div className="col-span-4 text-white/75 text-sm leading-relaxed">
                        {k.definition}
                    </div>
                    <div className="col-span-1">
                        <span className="inline-flex items-center px-2 py-1 border border-[var(--fora-red-1)] text-[var(--fora-red-1)] text-[10px] font-bold tracking-[0.18em] uppercase">
                            {k.target}
                        </span>
                    </div>
                    <div className="col-span-2 text-white/75 text-sm leading-relaxed">
                        {k.achieved ? (
                            <span className="inline-flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-full bg-[var(--fora-red-1)]" />
                                <span className="text-white font-semibold">
                                    {k.status}
                                </span>
                            </span>
                        ) : (
                            k.status
                        )}
                    </div>
                    <div className="col-span-2 text-white/75 text-sm leading-relaxed">
                        {k.focus}
                    </div>
                </div>
            ))}
        </div>

        {/* Mobile / tablet card layout */}
        <div
            className="lg:hidden grid sm:grid-cols-2 gap-4"
            data-testid={`${testIdPrefix}-cards`}
        >
            {rows.map((k) => (
                <article
                    key={k.n}
                    className="border border-white/15 p-6 hover:border-[var(--fora-red-1)] transition-colors"
                    data-testid={`${testIdPrefix}-card-${k.n}`}
                >
                    <div className="flex items-baseline justify-between">
                        <span className="text-[var(--fora-red-1)] font-[800] text-2xl tracking-[-0.04em] leading-none">
                            {k.n}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 border border-[var(--fora-red-1)] text-[var(--fora-red-1)] text-[10px] font-bold tracking-[0.18em] uppercase">
                            Target {k.target}
                        </span>
                    </div>
                    <h4 className="mt-3 font-[700] text-xl tracking-[-0.01em] leading-snug">
                        {k.name}
                    </h4>
                    <p className="mt-2 text-white/65 text-sm leading-relaxed">
                        {k.definition}
                    </p>
                    <div className="mt-5 space-y-3 text-sm">
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/55">
                                Current Status
                            </div>
                            <div
                                className={`mt-1 leading-relaxed ${
                                    k.achieved ? "text-white font-semibold" : "text-white/85"
                                }`}
                            >
                                {k.status}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/55">
                                Next Review Focus
                            </div>
                            <div className="mt-1 text-white/85 leading-relaxed">
                                {k.focus}
                            </div>
                        </div>
                    </div>
                </article>
            ))}
        </div>
    </div>
);

/* Cross-functional pill — flags an item that spans multiple reports.
   Use as: `<CrossFunctional />` inline next to a milestone/priority text. */
export const CrossFunctional = ({ size = "sm" }) => {
    const cls =
        size === "xs"
            ? "px-1.5 py-0.5 text-[8px]"
            : "px-2 py-0.5 text-[9px]";
    return (
        <span
            className={`inline-flex items-center gap-1 ${cls} bg-[var(--fora-red-1)]/15 text-[var(--fora-red-1)] border border-[var(--fora-red-1)]/40 font-bold tracking-[0.18em] uppercase whitespace-nowrap`}
            data-testid="cross-functional-badge"
        >
            <svg
                width="8"
                height="8"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
                <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Joint
        </span>
    );
};

/* Section header — used at the top of every numbered section. */
export const SectionHeader = ({ num, eyebrow, title, lead, dark = false }) => (
    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
            <div className="flex items-center gap-3">
                <span className="red-dot" />
                <span
                    className={`section-num ${dark ? "text-white/80" : ""}`}
                >
                    {num} — {eyebrow}
                </span>
            </div>
            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                {title}
            </h2>
        </div>
        {lead && (
            <p
                className={`max-w-md text-base ${
                    dark ? "text-white/70" : "text-[var(--fora-muted)]"
                }`}
            >
                {lead}
            </p>
        )}
    </div>
);
